import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, RotateCw, Trash2 } from "lucide-react";
import { adminEmailLog, adminRetryEmail, adminListSuppressions, adminRemoveSuppression } from "@/lib/admin-email.functions";

export const Route = createFileRoute("/_authenticated/admin/emails")({
  head: () => ({ meta: [{ title: "Email delivery — Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminEmailsPage,
});

type LogData = Awaited<ReturnType<typeof adminEmailLog>>;
type SuppData = Awaited<ReturnType<typeof adminListSuppressions>>;

function statusBadge(status: string | null) {
  if (!status) return <Badge variant="outline">unknown</Badge>;
  if (status === "sent") return <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 border-emerald-500/30">sent</Badge>;
  if (status === "dlq" || status === "failed") return <Badge variant="destructive">failed</Badge>;
  if (status === "suppressed" || status === "bounced" || status === "complained")
    return <Badge className="bg-amber-500/15 text-amber-800 hover:bg-amber-500/15 border-amber-500/30">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function AdminEmailsPage() {
  const fetchLog = useServerFn(adminEmailLog);
  const retry = useServerFn(adminRetryEmail);
  const listSupp = useServerFn(adminListSuppressions);
  const removeSupp = useServerFn(adminRemoveSuppression);

  const [tab, setTab] = useState<"log" | "suppressions">("log");
  const [days, setDays] = useState(7);
  const [template, setTemplate] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState<LogData | null>(null);
  const [supp, setSupp] = useState<SuppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadLog = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetchLog({ data: { days, template: template || undefined, status: status || undefined, limit: 100 } });
      setData(res);
    } catch (e) { setErr(String(e)); }
    finally { setLoading(false); }
  };
  const loadSupp = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await listSupp({ data: { limit: 200 } });
      setSupp(res);
    } catch (e) { setErr(String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === "log") void loadLog();
    else void loadSupp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, days, template, status]);

  const onRetry = async (messageId: string | null) => {
    if (!messageId) return;
    setBusyId(messageId); setErr(null);
    try {
      await retry({ data: { messageId } });
      await loadLog();
    } catch (e) { setErr(String(e)); }
    finally { setBusyId(null); }
  };

  const onRemoveSupp = async (email: string) => {
    setBusyId(email); setErr(null);
    try {
      await removeSupp({ data: { email } });
      await loadSupp();
    } catch (e) { setErr(String(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Email delivery</h1>
            <p className="mt-1 text-sm text-muted-foreground">Deduplicated by message id. Open/click counters update as recipients interact.</p>
          </div>
          <Button variant="outline" size="sm" onClick={tab === "log" ? loadLog : loadSupp} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        <div className="mt-4 inline-flex rounded-full border border-border bg-card p-1 text-sm">
          <button onClick={() => setTab("log")} className={`rounded-full px-3 py-1 ${tab === "log" ? "bg-foreground text-background" : ""}`}>Delivery log</button>
          <button onClick={() => setTab("suppressions")} className={`rounded-full px-3 py-1 ${tab === "suppressions" ? "bg-foreground text-background" : ""}`}>Suppressions</button>
        </div>

        {err && <p className="mt-4 text-sm text-destructive">{err}</p>}

        {tab === "log" && (
          <>
            <div className="mt-6 flex flex-wrap gap-2">
              {[1, 7, 30].map(d => (
                <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>
                  {d === 1 ? "Last 24h" : `${d} days`}
                </Button>
              ))}
              <select className="rounded-md border border-border bg-card px-3 py-1.5 text-sm" value={template} onChange={e => setTemplate(e.target.value)}>
                <option value="">All templates</option>
                {data?.templates.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="rounded-md border border-border bg-card px-3 py-1.5 text-sm" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="dlq">Failed (DLQ)</option>
                <option value="suppressed">Suppressed</option>
                <option value="bounced">Bounced</option>
                <option value="complained">Complained</option>
              </select>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { k: "total", label: "Unique" },
                { k: "sent", label: "Sent" },
                { k: "opened", label: "Opened" },
                { k: "clicked", label: "Clicked" },
                { k: "failed", label: "Failed" },
                { k: "suppressed", label: "Suppressed" },
              ].map(s => (
                <div key={s.k} className="rounded-2xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="mt-1 text-2xl font-bold">{data?.counts[s.k as keyof typeof data.counts] ?? 0}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Template</th>
                    <th className="px-3 py-2 text-left">Recipient</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Opens</th>
                    <th className="px-3 py-2 text-left">Clicks</th>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.rows.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No emails in this range.</td></tr>
                  )}
                  {data?.rows.map(r => {
                    const canRetry = r.status === "dlq" || r.status === "failed";
                    return (
                      <tr key={r.id} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-medium">{r.template_name ?? "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.recipient_email ?? "—"}</td>
                        <td className="px-3 py-2">
                          {statusBadge(r.status)}
                          {r.error_message && <p className="mt-1 text-xs text-destructive">{r.error_message}</p>}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{r.open_count ?? 0}</td>
                        <td className="px-3 py-2 tabular-nums">{r.click_count ?? 0}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                        <td className="px-3 py-2">
                          {canRetry && r.message_id && (
                            <Button size="sm" variant="outline" disabled={busyId === r.message_id} onClick={() => onRetry(r.message_id)}>
                              {busyId === r.message_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                              <span className="ml-1">Retry</span>
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              Queue retries failed sends with exponential backoff (up to 5 attempts) before moving to DLQ.
              Admin retry re-renders the template and re-enqueues with the original idempotency key.
              Bounce and complaint webhooks auto-add recipients to the suppression list; single soft bounces auto-recover after 90 days.
            </p>
          </>
        )}

        {tab === "suppressions" && (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-left">Events</th>
                  <th className="px-3 py-2 text-left">Last event</th>
                  <th className="px-3 py-2 text-left">First added</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {supp?.rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No suppressed recipients.</td></tr>
                )}
                {supp?.rows.map(r => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{r.email}</td>
                    <td className="px-3 py-2"><Badge variant="outline">{r.reason}</Badge></td>
                    <td className="px-3 py-2 tabular-nums">{r.bounce_count}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.last_event_at).toLocaleString("en-GB")}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" disabled={busyId === r.email} onClick={() => onRemoveSupp(r.email)}>
                        {busyId === r.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        <span className="ml-1">Remove</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
